'use client'

import { useState } from 'react'
import { Button, Card, Divider, Icon, Spacer } from '@vinted/web-ui'
import { LocationArrow16, Minus16, Plus16 } from '@vinted/monochrome-icons'
import { noop } from 'lodash'

import { useTranslate } from '@marketplace-web/shared/i18n'
import { CoordinatesModel } from '@marketplace-web/domain/checkout-shipping-data'

import { getLocationCoordinates } from '../../../../utils/location-utils'

type Props = {
  onZoomIn: () => void
  onZoomOut: () => void
  onLocationResponse: (coordinates: CoordinatesModel) => void
}

const Controls = ({ onZoomIn = noop, onZoomOut = noop, onLocationResponse = noop }: Props) => {
  const translate = useTranslate('checkout.shipping.pickup_point_modal.map')

  const [isLocationLoading, setIsLocationLoading] = useState(false)

  const handleLocationRequest = async (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    if (isLocationLoading) {
      event.preventDefault()

      return
    }

    try {
      setIsLocationLoading(true)

      const currentPositionCoordinates = await getLocationCoordinates()

      if (!currentPositionCoordinates) return

      onLocationResponse(currentPositionCoordinates)
    } finally {
      setIsLocationLoading(false)
    }
  }

  return (
    <div className="pickup-point-map__controls">
      <Card styling={Card.Styling.Lifted}>
        <Button
          size={Button.Size.Small}
          icon={
            <Icon
              name={Plus16}
              color={Icon.Color.GreyscaleLevel2}
              aria={{
                'aria-label': translate('a11y.zoom_in'),
              }}
            />
          }
          onClick={onZoomIn}
          styling={Button.Styling.Flat}
          testId="map-controls-zoom-in"
        />
        <Divider />
        <Button
          size={Button.Size.Small}
          icon={
            <Icon
              name={Minus16}
              color={Icon.Color.GreyscaleLevel2}
              aria={{
                'aria-label': translate('a11y.zoom_out'),
              }}
            />
          }
          onClick={onZoomOut}
          styling={Button.Styling.Flat}
          testId="map-controls-zoom-out"
        />
      </Card>
      <Spacer />
      <Card styling={Card.Styling.Lifted}>
        <Button
          size={Button.Size.Medium}
          icon={
            <Icon
              name={LocationArrow16}
              color={Icon.Color.Primary}
              aria={{
                'aria-label': translate('a11y.location'),
              }}
            />
          }
          onClick={handleLocationRequest}
          theme="primary"
          styling={Button.Styling.Flat}
          isLoading={isLocationLoading}
          testId={`map-controls-location${isLocationLoading ? '-loading' : ''}`}
        />
      </Card>
    </div>
  )
}

export default Controls
